import React, { useState } from 'react';
import { Building2, LogOut, Globe, Plus, X, ChevronRight, ChevronLeft, Check, Sparkles, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../shared/services/apiClient';

interface OnboardingEmpresaProps {
  currentUser: any;
  onComplete: (empresaData: any) => void;
  onLogout?: () => void;
}

// 🌍 Tipo de identificador fiscal por país (DISEÑO FUTURO - no persiste aún)
interface FiscalIdentifier {
  type: string;
  value: string;
  country?: string;
}

/**
 * Componente de Onboarding WIZARD para usuarios EXTERNAL sin empresa asignada
 * 
 * PASOS:
 * 1. Identidad de la Empresa (obligatorio)
 * 2. Datos Fiscales (opcional)
 * 3. Primer Acción (qué querés hacer primero)
 * 
 * ⚠️ IMPORTANTE: Solo persiste name, cuit, rubro, address, phone
 * El resto es UX/visual para reducir fricción
 */
const OnboardingEmpresa: React.FC<OnboardingEmpresaProps> = ({ currentUser, onComplete, onLogout }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [form, setForm] = useState({
    name: '',
    country: '',
    rubro: '',
    address: '',
    phone: '',
  });

  // 🌍 DISEÑO FUTURO: Múltiples identificadores fiscales (solo UI por ahora)
  const [fiscalIdentifiers, setFiscalIdentifiers] = useState<FiscalIdentifier[]>([]);
  const [showFiscalForm, setShowFiscalForm] = useState(false);
  const [newFiscalId, setNewFiscalId] = useState({ type: 'CUIT', value: '', country: '' });
  const [skipFiscalData, setSkipFiscalData] = useState(false);

  // Paso 3: Primera acción sugerida
  const [suggestedAction, setSuggestedAction] = useState<'dashboard' | 'datasource' | ''>('');

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) {
      onLogout();
    } else {
      window.location.href = '/';
    }
  };

  // 🌍 Países/Regiones comunes
  const countriesOptions = [
    { value: 'AR', label: '🇦🇷 Argentina' },
    { value: 'BR', label: '🇧🇷 Brasil' },
    { value: 'CL', label: '🇨🇱 Chile' },
    { value: 'CO', label: '🇨🇴 Colombia' },
    { value: 'MX', label: '🇲🇽 México' },
    { value: 'PE', label: '🇵🇪 Perú' },
    { value: 'UY', label: '🇺🇾 Uruguay' },
    { value: 'US', label: '🇺🇸 Estados Unidos' },
    { value: 'ES', label: '🇪🇸 España' },
    { value: 'OTHER', label: '🌍 Otro' },
  ];

  // 📋 Tipos de identificadores fiscales
  const fiscalIdTypes = [
    { value: 'CUIT', label: 'CUIT (Argentina)', countries: ['AR'] },
    { value: 'CUIL', label: 'CUIL (Argentina)', countries: ['AR'] },
    { value: 'CNPJ', label: 'CNPJ (Brasil)', countries: ['BR'] },
    { value: 'RFC', label: 'RFC (México)', countries: ['MX'] },
    { value: 'RUT', label: 'RUT (Chile, Colombia)', countries: ['CL', 'CO'] },
    { value: 'NIT', label: 'NIT (Colombia)', countries: ['CO'] },
    { value: 'RUC', label: 'RUC (Perú)', countries: ['PE'] },
    { value: 'VAT', label: 'VAT/IVA (Europa)', countries: ['ES', 'OTHER'] },
    { value: 'EIN', label: 'EIN (Estados Unidos)', countries: ['US'] },
    { value: 'TAX_ID', label: 'Tax ID / Otro', countries: ['OTHER'] },
  ];

  // Opciones de rubro/industria
  const rubrosOptions = [
    'Agricultura y Ganadería',
    'Alimentos y Bebidas',
    'Automotriz',
    'Construcción',
    'Educación',
    'Energía',
    'Finanzas y Seguros',
    'Manufactura',
    'Retail y Comercio',
    'Salud',
    'Servicios Profesionales',
    'Tecnología',
    'Transporte y Logística',
    'Turismo y Hotelería',
    'Otro'
  ];

  // 🌍 DISEÑO FUTURO: Agregar identificador fiscal
  const handleAddFiscalId = () => {
    if (!newFiscalId.value.trim()) {
      toast.error('Ingresa el número de identificador');
      return;
    }
    setFiscalIdentifiers([...fiscalIdentifiers, { ...newFiscalId }]);
    setNewFiscalId({ type: 'CUIT', value: '', country: form.country });
    setShowFiscalForm(false);
    toast.success('Identificador agregado');
  };

  // 🌍 DISEÑO FUTURO: Remover identificador fiscal
  const handleRemoveFiscalId = (index: number) => {
    setFiscalIdentifiers(fiscalIdentifiers.filter((_, i) => i !== index));
  };

  // Validar si puede avanzar al siguiente paso
  const canGoToStep2 = form.name.trim() && form.country && form.rubro;
  const canGoToStep3 = true; // Paso 2 es opcional
  const canSubmit = canGoToStep2; // Solo necesita paso 1 completado

  const handleNext = () => {
    if (currentStep === 1 && !canGoToStep2) {
      toast.error('Completá los datos básicos de tu empresa para continuar');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // ✅ Validaciones obligatorias
    if (!form.name.trim()) {
      toast.error('El nombre de la empresa es requerido');
      return;
    }

    if (!form.country) {
      toast.error('Selecciona el país o región de tu empresa');
      return;
    }

    if (!form.rubro) {
      toast.error('Selecciona el rubro de tu empresa');
      return;
    }

    try {
      // 🚀 COMPATIBILIDAD BACKEND: Enviamos datos en formato actual
      // - cuit: se envía como string vacío o primer identificador fiscal
      // - country, fiscalIdentifiers: se ignoran por ahora (backend no los soporta)
      const payload = {
        name: form.name,
        cuit: fiscalIdentifiers.length > 0 ? fiscalIdentifiers[0].value : '', // ⚠️ Compatibilidad
        rubro: form.rubro,
        address: form.address || undefined,
        phone: form.phone || undefined,
        // 🌍 DISEÑO FUTURO: Descomentar cuando backend soporte estos campos
        // country: form.country,
        // fiscalIdentifiers: fiscalIdentifiers,
      };

      console.log('📤 [Onboarding] Sending payload:', payload);

      const response = await api.post('/companies', payload);

      const data = response.data;

      // Backend retorna empresa + user actualizado + NEW TOKEN
      const updatedUser = data.user || {
        ...currentUser,
        empresaId: data.id,
      };

      // 🔑 CRITICAL: Update token with new empresaId
      if (data.token) {
        console.log('🔑 [Onboarding] NEW JWT received with empresaId:', updatedUser.empresaId);
        localStorage.setItem('token', data.token);
        
        // ✅ Update axios default headers to use new token immediately
        // This ensures the next API call will use the updated JWT
        if (api.defaults.headers) {
          api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }
        
        console.log('✅ [Onboarding] Token updated in localStorage and axios headers');
      } else {
        console.warn('⚠️ [Onboarding] Backend did not return new token');
      }

      // ✅ Guardar empresaId en localStorage para uso en componentes
      if (updatedUser.empresaId) {
        localStorage.setItem('empresaId', updatedUser.empresaId);
        console.log('✅ empresaId saved to localStorage:', updatedUser.empresaId);
      }

      // Actualizar localStorage con datos del usuario actualizado
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));

      toast.success('¡Bienvenido! Tu empresa ha sido registrada.');
      onComplete(updatedUser);

    } catch (error: any) {
      console.error('Error en onboarding:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error completando el registro';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-3xl w-full bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden">
        
        {/* ═══════════════════════════════════════════════════════════ */}
        {/* WIZARD HEADER CON BARRA DE PROGRESO */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-sky-900/20 to-purple-900/20 border-b border-slate-800 p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-sky-500/10 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">¡Bienvenido a AION BI!</h1>
                <p className="text-slate-400 text-sm">Paso {currentStep} de {totalSteps}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>

          {/* Barra de progreso con pasos */}
          <div className="flex items-center justify-between relative">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center relative z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    step < currentStep 
                      ? 'bg-sky-500 text-white' 
                      : step === currentStep 
                      ? 'bg-sky-500 text-white ring-4 ring-sky-500/30' 
                      : 'bg-slate-800 text-slate-500'
                  }`}>
                    {step < currentStep ? <Check className="w-5 h-5" /> : step}
                  </div>
                  <p className={`text-xs mt-2 font-medium ${
                    step === currentStep ? 'text-white' : 'text-slate-500'
                  }`}>
                    {step === 1 && 'Empresa'}
                    {step === 2 && 'Datos Fiscales'}
                    {step === 3 && 'Comenzar'}
                  </p>
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step < currentStep ? 'bg-sky-500' : 'bg-slate-800'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* User Info Badge */}
        <div className="bg-slate-800/50 border-b border-slate-800 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-sky-500/10 rounded-full flex items-center justify-center">
                <span className="text-sky-400 text-sm font-semibold">
                  {currentUser?.name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <p className="text-sm text-white font-medium">{currentUser?.email}</p>
                <p className="text-xs text-sky-400">Usuario EXTERNAL</p>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* CONTENIDO DEL PASO ACTUAL */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <div className="p-8">
          {currentStep === 1 && (
            <Step1IdentidadEmpresa 
              form={form}
              setForm={setForm}
              rubrosOptions={rubrosOptions}
              countriesOptions={countriesOptions}
            />
          )}

          {currentStep === 2 && (
            <Step2DatosFiscales
              form={form}
              setForm={setForm}
              fiscalIdentifiers={fiscalIdentifiers}
              setFiscalIdentifiers={setFiscalIdentifiers}
              showFiscalForm={showFiscalForm}
              setShowFiscalForm={setShowFiscalForm}
              newFiscalId={newFiscalId}
              setNewFiscalId={setNewFiscalId}
              fiscalIdTypes={fiscalIdTypes}
              skipFiscalData={skipFiscalData}
              setSkipFiscalData={setSkipFiscalData}
              handleAddFiscalId={handleAddFiscalId}
              handleRemoveFiscalId={handleRemoveFiscalId}
            />
          )}

          {currentStep === 3 && (
            <Step3PrimeraAccion
              suggestedAction={suggestedAction}
              setSuggestedAction={setSuggestedAction}
            />
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* NAVEGACIÓN DEL WIZARD */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <div className="border-t border-slate-800 bg-slate-900/50 px-8 py-6 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Atrás
          </button>

          <div className="flex gap-3">
            {currentStep < totalSteps ? (
              <>
                {currentStep === 2 && (
                  <button
                    onClick={() => {
                      setSkipFiscalData(true);
                      handleNext();
                    }}
                    className="px-5 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors text-sm"
                  >
                    Omitir
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={currentStep === 1 && !canGoToStep2}
                  className="flex items-center gap-2 px-6 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-all disabled:cursor-not-allowed"
                >
                  Continuar
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-sky-500 to-purple-600 hover:from-sky-600 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-all disabled:cursor-not-allowed shadow-lg"
              >
                <Sparkles className="w-5 h-5" />
                Comenzar a usar AION BI
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

{/* ═══════════════════════════════════════════════════════════ */}
{/* COMPONENTES DE CADA PASO */}
{/* ═══════════════════════════════════════════════════════════ */}

// PASO 1: Identidad de la Empresa
const Step1IdentidadEmpresa: React.FC<any> = ({ form, setForm, rubrosOptions, countriesOptions }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Configurá tu empresa</h2>
      <p className="text-slate-400">
        Estos datos nos ayudan a personalizar dashboards, análisis y métricas según tu negocio
      </p>
    </div>

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Nombre de la Empresa <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all"
          placeholder="Ej: Mi Empresa S.A."
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <p className="text-xs text-slate-500 mt-1.5">
          💡 Así identificaremos tu espacio de trabajo
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          País o Región <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <select
            className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all appearance-none"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          >
            <option value="">Selecciona un país</option>
            {countriesOptions.map((country: any) => (
              <option key={country.value} value={country.value}>{country.label}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          💡 Determina formatos de moneda, fechas y reportes regionales
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Rubro o Industria <span className="text-red-400">*</span>
        </label>
        <select
          className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all"
          value={form.rubro}
          onChange={(e) => setForm({ ...form, rubro: e.target.value })}
        >
          <option value="">Selecciona un rubro</option>
          {rubrosOptions.map((rubro: string) => (
            <option key={rubro} value={rubro}>{rubro}</option>
          ))}
        </select>
        <p className="text-xs text-slate-500 mt-1.5">
          💡 Personalizamos métricas, KPIs y benchmarks según tu industria
        </p>
      </div>
    </div>

    <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
      <p className="text-sm text-sky-300 font-medium mb-1">✨ Datos opcionales</p>
      <p className="text-xs text-slate-400">
        Podés agregar información adicional (dirección, teléfono, datos fiscales) en el siguiente paso. No es obligatorio para comenzar.
      </p>
    </div>
  </div>
);

// PASO 2: Datos Fiscales (Opcional)
const Step2DatosFiscales: React.FC<any> = ({ 
  form, setForm, fiscalIdentifiers, setFiscalIdentifiers, showFiscalForm, setShowFiscalForm,
  newFiscalId, setNewFiscalId, fiscalIdTypes, skipFiscalData, setSkipFiscalData,
  handleAddFiscalId, handleRemoveFiscalId
}) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Datos Fiscales y Contacto</h2>
      <p className="text-slate-400">
        Estos datos son completamente opcionales. Podés cargarlos ahora o más adelante desde Configuración.
      </p>
    </div>

    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
      <p className="text-sm text-amber-300 font-medium mb-1">💡 Paso opcional</p>
      <p className="text-xs text-slate-400">
        Podés omitir este paso y empezar a usar la plataforma inmediatamente. Estos datos se pueden agregar después.
      </p>
    </div>

    {/* Identificadores Fiscales */}
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-3">
        Identificadores Fiscales (opcional)
      </label>
      <p className="text-xs text-slate-500 mb-4">
        Ej: CUIT, RFC, VAT, NIT, RUC, EIN... según tu país. No es obligatorio para usar AION BI.
      </p>

      {/* Lista de identificadores */}
      {fiscalIdentifiers.length > 0 && (
        <div className="space-y-2 mb-4">
          {fiscalIdentifiers.map((id: any, index: number) => (
            <div key={index} className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{id.type}</p>
                <p className="text-xs text-slate-400">{id.value}</p>
              </div>
              <button
                onClick={() => handleRemoveFiscalId(index)}
                className="text-slate-400 hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Formulario agregar ID */}
      {showFiscalForm ? (
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Tipo de Identificador
            </label>
            <select
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-sky-500 outline-none"
              value={newFiscalId.type}
              onChange={(e) => setNewFiscalId({ ...newFiscalId, type: e.target.value })}
            >
              {fiscalIdTypes
                .filter((type: any) => 
                  !form.country || 
                  type.countries.includes(form.country) || 
                  type.countries.includes('OTHER')
                )
                .map((type: any) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Número</label>
            <input
              type="text"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-sky-500 outline-none"
              placeholder="Ej: 20-12345678-9"
              value={newFiscalId.value}
              onChange={(e) => setNewFiscalId({ ...newFiscalId, value: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleAddFiscalId()}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleAddFiscalId}
              className="flex-1 py-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Agregar
            </button>
            <button
              onClick={() => {
                setShowFiscalForm(false);
                setNewFiscalId({ type: 'CUIT', value: '', country: form.country });
              }}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowFiscalForm(true)}
          disabled={!form.country}
          className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-700 hover:border-sky-500 text-slate-400 hover:text-sky-400 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Agregar Identificador Fiscal</span>
        </button>
      )}

      {!form.country && (
        <p className="text-xs text-amber-500/80 mt-2">
          ⚠️ Selecciona primero el país en el paso anterior
        </p>
      )}
    </div>

    {/* Información adicional */}
    <div className="space-y-4 pt-4 border-t border-slate-800">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Dirección (opcional)</label>
        <input
          type="text"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-sky-500 outline-none"
          placeholder="Calle 123, Ciudad"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Teléfono (opcional)</label>
        <input
          type="text"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-sky-500 outline-none"
          placeholder="+54 11 1234-5678"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>
    </div>
  </div>
);

// PASO 3: Primera Acción Sugerida
const Step3PrimeraAccion: React.FC<any> = ({ suggestedAction, setSuggestedAction }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">¿Qué querés hacer primero?</h2>
      <p className="text-slate-400">
        Elegí tu primera acción para empezar. Podés hacer ambas después, esto solo te ayuda a enfocarte al entrar.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Opción 1: Crear Dashboard */}
      <button
        onClick={() => setSuggestedAction('dashboard')}
        className={`p-6 rounded-xl border-2 text-left transition-all ${
          suggestedAction === 'dashboard'
            ? 'border-sky-500 bg-sky-500/10'
            : 'border-slate-700 bg-slate-800/50 hover:border-sky-500/50'
        }`}
      >
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
          suggestedAction === 'dashboard' ? 'bg-sky-500/20' : 'bg-slate-700'
        }`}>
          <BarChart3 className={`w-6 h-6 ${suggestedAction === 'dashboard' ? 'text-sky-400' : 'text-slate-400'}`} />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Crear un Dashboard
        </h3>
        <p className="text-sm text-slate-400">
          Empezá visualizando tus datos con dashboards profesionales y personalizables
        </p>
      </button>

      {/* Opción 2: Conectar Fuente de Datos */}
      <button
        onClick={() => setSuggestedAction('datasource')}
        className={`p-6 rounded-xl border-2 text-left transition-all ${
          suggestedAction === 'datasource'
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-slate-700 bg-slate-800/50 hover:border-purple-500/50'
        }`}
      >
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
          suggestedAction === 'datasource' ? 'bg-purple-500/20' : 'bg-slate-700'
        }`}>
          <Globe className={`w-6 h-6 ${suggestedAction === 'datasource' ? 'text-purple-400' : 'text-slate-400'}`} />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Conectar Fuente de Datos
        </h3>
        <p className="text-sm text-slate-400">
          Conectá APIs, Google Sheets u otras fuentes para empezar a analizar información
        </p>
      </button>
    </div>

    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
      <p className="text-sm text-slate-300 font-medium mb-1">💡 Consejo</p>
      <p className="text-xs text-slate-400">
        Si todavía no tenés datos, podés explorar con datos de ejemplo. No te preocupes, esto es solo orientativo.
      </p>
    </div>
  </div>
);

export default OnboardingEmpresa;
